import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import ConfirmationModal from '../../../shared/components/ui/ConfirmationModal';
import { FriendshipTier } from '../types/friends';

interface EditFriendshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (tier: FriendshipTier, note?: string) => Promise<void>;
  onRequestHigher: (tier: FriendshipTier, note?: string) => Promise<void>;
  friendName: string;
  currentTier: FriendshipTier;
}

const TIERS: { id: FriendshipTier; label: string; description: string; color: string; icon: string }[] = [
  {
    id: 'TIER_1',
    label: 'Gold (Tier 1)',
    description: 'All %s and charts visible, $ values hidden',
    color: 'text-yellow-500',
    icon: '★'
  },
  {
    id: 'TIER_2',
    label: 'Silver (Tier 2)',
    description: 'Portfolio and account %s visible, individual ticker data hidden',
    color: 'text-gray-400',
    icon: '★'
  },
  {
    id: 'TIER_3',
    label: 'Bronze (Tier 3)',
    description: 'Portfolio %s visible, account and ticker data hidden',
    color: '', // Custom color #A75217 applied via inline style
    icon: '★'
  },
  {
    id: 'BASIC',
    label: 'Basic',
    description: 'All data hidden, only messaging allowed',
    color: 'text-gray-300',
    icon: '★'
  }
];

const TIER_ORDER: FriendshipTier[] = ['TIER_1', 'TIER_2', 'TIER_3', 'BASIC'];

// Helper to compare tiers (higher tier = lower index)
const isHigherTier = (tier1: FriendshipTier, tier2: FriendshipTier): boolean => {
  const index1 = TIER_ORDER.indexOf(tier1);
  const index2 = TIER_ORDER.indexOf(tier2);
  return index1 < index2;
};

const getTierLabel = (tier: FriendshipTier): string => {
  const tierObj = TIERS.find(t => t.id === tier);
  return tierObj?.label || tier;
};

export const EditFriendshipModal: React.FC<EditFriendshipModalProps> = ({ 
  isOpen, 
  onClose, 
  onUpdate, 
  onRequestHigher,
  friendName, 
  currentTier 
}) => {
  const [selectedTier, setSelectedTier] = useState<FriendshipTier>(currentTier);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLowerTierConfirm, setShowLowerTierConfirm] = useState(false);
  const [showHigherTierConfirm, setShowHigherTierConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset selected tier when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTier(currentTier);
      setNote('');
      setErrorMessage(null);
    }
  }, [isOpen, currentTier]);

  const handleSubmit = async () => {
    if (selectedTier === currentTier) {
      onClose();
      return;
    }

    const isLower = isHigherTier(currentTier, selectedTier);
    const isHigher = isHigherTier(selectedTier, currentTier);

    if (isLower) {
      // Show confirmation for lowering tier
      setShowLowerTierConfirm(true);
    } else if (isHigher) {
      // Show confirmation for requesting higher tier
      setShowHigherTierConfirm(true);
    }
  };

  const handleConfirmLowerTier = async () => {
    setIsSubmitting(true);
    try {
      await onUpdate(selectedTier, note);
      setShowLowerTierConfirm(false);
      onClose();
      setNote('');
    } catch (error) {
      console.error('Failed to update friendship tier:', error);
      // Keep modal open on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmHigherTier = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await onRequestHigher(selectedTier, note);
      setShowHigherTierConfirm(false);
      onClose();
      setNote('');
    } catch (error) {
      console.error('Failed to request higher tier:', error);
      const message = error instanceof Error ? error.message : 'Failed to request higher tier';
      setErrorMessage(message);
      // Keep confirmation modal open on error so user can see the message
    } finally {
      setIsSubmitting(false);
    }
  };

  const isHigher = selectedTier !== currentTier && isHigherTier(selectedTier, currentTier);

  return (
    <>
      <Modal isOpen={isOpen && !showLowerTierConfirm && !showHigherTierConfirm} onClose={onClose} title={`Edit Friendship with ${friendName}`} size="md">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Choose Friendship Tier:</label>
            <div className="space-y-3">
              {TIERS.map((tier) => {
                const isCurrentTier = tier.id === currentTier;
                const isSelected = selectedTier === tier.id;
                const isDisabled = isCurrentTier;
                
                return (
                  <div
                    key={tier.id}
                    onClick={() => !isDisabled && setSelectedTier(tier.id)}
                    className={`
                      relative flex items-start p-4 rounded-lg border transition-all
                      ${isDisabled 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                        : isSelected 
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500 cursor-pointer' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'}
                    `}
                  >
                    <div className="flex items-center h-5">
                      <input
                        type="radio"
                        name="tier"
                        checked={isSelected}
                        onChange={() => !isDisabled && setSelectedTier(tier.id)}
                        disabled={isDisabled}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                      />
                    </div>
                    <div className="ml-3 text-sm flex-1">
                      <label className={`font-medium ${isSelected && !isDisabled ? 'text-blue-900' : 'text-gray-900'}`}>
                        <span 
                          className={`mr-1 ${tier.color || ''}`}
                          style={tier.id === 'TIER_3' ? { color: '#A75217' } : undefined}
                        >
                          {tier.icon}
                        </span>
                        {tier.label}
                        {isCurrentTier && (
                          <span className="ml-2 text-xs text-gray-500 font-normal">(Current tier)</span>
                        )}
                      </label>
                      <p className={`mt-1 ${isSelected && !isDisabled ? 'text-blue-700' : 'text-gray-500'}`}>
                        {tier.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
              Add a note (optional)
            </label>
            <textarea
              id="note"
              rows={3}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || selectedTier === currentTier}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Processing...' : isHigher ? 'Request Higher Tier' : 'Update Tier'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Lower Tier Confirmation */}
      <ConfirmationModal
        isOpen={showLowerTierConfirm}
        onClose={() => {
          if (!isSubmitting) {
            setShowLowerTierConfirm(false);
          }
        }}
        title="Lower Friendship Tier"
        message={
          <span>
            Are you sure you want to lower your friendship tier to <strong>{getTierLabel(selectedTier)}</strong>? 
            Doing so will be automatic, and moving back up to a higher tier will require approval from both friends.
          </span>
        }
        confirmLabel="Lower Tier"
        cancelLabel="Cancel"
        onConfirm={handleConfirmLowerTier}
        isLoading={isSubmitting}
      />

      {/* Higher Tier Confirmation */}
      <ConfirmationModal
        isOpen={showHigherTierConfirm}
        onClose={() => {
          if (!isSubmitting) {
            setShowHigherTierConfirm(false);
            setErrorMessage(null);
          }
        }}
        title="Requesting Higher Tier"
        message={
          <div className="space-y-3">
            <span>
              Are you sure you want to request a higher tier? If so this requires approval from <strong>{friendName}</strong>.
            </span>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmLabel="Send Request"
        cancelLabel="Cancel"
        onConfirm={handleConfirmHigherTier}
        isLoading={isSubmitting}
      />
    </>
  );
};

